'use client';

import { useCallback, useMemo, useRef, useState } from 'react';
import { Box, Button, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';

import { useLanguage } from '@/contexts/LanguageContext';
import { useT } from '@/hooks';
import { BRAND } from './onboarding.constants';
import { TradeDemo } from './TradeDemo';

import { SUPPORTED_LANGUAGES } from '@/constants/language.constants';

interface OnboardingScreenProps {
  /** Fired after the trade-demo finale — reveals the trending/home page. */
  onComplete: () => void;
  onSkip: () => void;
}

// Text slides. Copy lives in i18n under `onboarding.slides.<key>`. A final
// interactive "trade demo" step is appended after these (see TOTAL_STEPS).
const SLIDE_KEYS = ['predict', 'markets', 'start'] as const;
const TOTAL_STEPS = SLIDE_KEYS.length + 1; // + the trade demo
const DEMO_INDEX = SLIDE_KEYS.length;

/** Porvenix wordmark logo mark — circle with a horizontal line (matches `Logo`). */
const LogoMark = ({ size }: { size: number }) => (
  <Box
    component="svg"
    width={size}
    height={size}
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    sx={{ display: 'block', flexShrink: 0 }}
  >
    <circle
      cx="16"
      cy="16"
      r="14"
      stroke="#ffffff"
      strokeWidth="3"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <line x1="4" y1="16" x2="28" y2="16" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" />
  </Box>
);

/** Porvenix app icon — blue rounded square with the white ring + line (favicon). */
const AppIcon = ({ size }: { size: number }) => (
  <Box
    component="svg"
    width={size}
    height={size}
    viewBox="0 0 128 128"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    sx={{ display: 'block', flexShrink: 0 }}
  >
    <rect x="0" y="0" width="128" height="128" rx="30" fill={BRAND.blue} />
    <circle cx="64" cy="64" r="34" fill="none" stroke="#ffffff" strokeWidth="10" />
    <rect x="30" y="59" width="68" height="10" rx="5" fill="#ffffff" />
  </Box>
);

/**
 * Segmented EN/ES language toggle for the always-dark onboarding top bar.
 * Uses the BRAND palette (not the MUI theme) since the splash renders dark
 * regardless of the user's light/dark preference.
 */
const LangToggle = () => {
  const { language, setLanguage } = useLanguage();
  return (
    <Stack
      direction="row"
      role="group"
      aria-label="Select language"
      sx={{
        p: '3px',
        borderRadius: 999,
        bgcolor: 'rgba(255,255,255,0.06)',
        border: `1px solid ${BRAND.cardBorder}`,
      }}
    >
      {SUPPORTED_LANGUAGES.map((lang) => {
        const isSelected = lang.code === language.code;
        return (
          <Button
            key={lang.code}
            onClick={() => setLanguage(lang.code)}
            disableRipple
            aria-pressed={isSelected}
            sx={{
              minWidth: 'auto',
              px: { xs: 1.25, sm: 1.5 },
              py: 0.25,
              borderRadius: 999,
              fontSize: '0.75rem',
              fontWeight: 700,
              lineHeight: 1.4,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: isSelected ? '#ffffff' : BRAND.textMuted,
              bgcolor: isSelected ? BRAND.blue : 'transparent',
              transition: 'color 0.2s ease, background-color 0.2s ease',
              '&:hover': {
                bgcolor: isSelected ? BRAND.blueDark : 'rgba(255,255,255,0.06)',
                color: '#ffffff',
              },
            }}
          >
            {lang.code.toUpperCase()}
          </Button>
        );
      })}
    </Stack>
  );
};

export const OnboardingScreen = ({ onComplete, onSkip }: OnboardingScreenProps) => {
  const { t } = useT();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [active, setActive] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const slides = useMemo(
    () =>
      SLIDE_KEYS.map((key) => ({
        key,
        title: t(`onboarding.slides.${key}.title`),
        description: t(`onboarding.slides.${key}.description`),
        points: [
          t(`onboarding.slides.${key}.point1`),
          t(`onboarding.slides.${key}.point2`),
          t(`onboarding.slides.${key}.point3`),
        ],
      })),
    [t],
  );

  const goTo = useCallback((index: number) => {
    setActive(Math.min(Math.max(index, 0), TOTAL_STEPS - 1));
  }, []);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0]?.clientX ?? null;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current == null) return;
    const delta = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
    if (Math.abs(delta) > 48) goTo(active + (delta < 0 ? 1 : -1));
    touchStartX.current = null;
  };

  const isDemo = active === DEMO_INDEX;
  const slide = slides[active];
  const isCompare = slide?.key === 'start';
  // First two text slides say "Next"; the last text slide says "Get started".
  const ctaLabel =
    active < SLIDE_KEYS.length - 1
      ? t('onboarding.next', { defaultValue: 'Next' })
      : t('onboarding.getStarted', { defaultValue: 'Get started' });

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1250,
        bgcolor: BRAND.bg,
        // Ambient glow matches BRAND.blue (#3b82f6) — was amber (#e8b046).
        backgroundImage: `radial-gradient(130% 85% at 50% -12%, rgba(59, 130, 246,0.20), rgba(59, 130, 246,0) 46%)`,
        color: BRAND.textPrimary,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Top bar: brand + skip */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 2.5, sm: 4, md: 6 },
          py: { xs: 2, sm: 2.5 },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <LogoMark size={isMobile ? 26 : 30} />
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1.05rem', sm: '1.2rem' },
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: BRAND.textPrimary,
            }}
          >
            Porvenix
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={{ xs: 1, sm: 1.5 }}>
          <LangToggle />
          <Button
            onClick={onSkip}
            disableRipple
            sx={{
              color: BRAND.textMuted,
              fontSize: { xs: '0.9rem', sm: '1rem' },
              fontWeight: 500,
              textTransform: 'none',
              px: 1,
              minWidth: 'auto',
              '&:hover': { color: BRAND.textPrimary, bgcolor: 'transparent' },
            }}
          >
            {t('onboarding.skip')}
          </Button>
        </Stack>
      </Box>

      {/* Center content */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 2, sm: 3 },
          pb: { xs: 5, sm: 7 },
        }}
      >
        {/* Step indicator */}
        <Stack direction="row" spacing={1} sx={{ mb: { xs: 3, sm: 4 } }}>
          {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
            <Box
              key={i}
              role="button"
              aria-label={`Step ${i + 1}`}
              onClick={() => goTo(i)}
              sx={{
                height: 6,
                width: i === active ? 26 : 6,
                borderRadius: 999,
                cursor: 'pointer',
                bgcolor: i === active ? BRAND.blue : 'rgba(255,255,255,0.22)',
                transition: 'width 0.3s ease, background-color 0.3s ease',
              }}
            />
          ))}
        </Stack>

        {/* Card */}
        <Box
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          sx={{
            width: '100%',
            maxWidth: 560,
            bgcolor: BRAND.card,
            border: `1px solid ${BRAND.cardBorder}`,
            borderRadius: { xs: 3, sm: 4 },
            px: { xs: 2.5, sm: 5 },
            py: { xs: 4, sm: 5 },
            boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
            textAlign: 'center',
          }}
        >
          {isDemo ? (
            <TradeDemo onComplete={onComplete} />
          ) : (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 2.5, sm: 3 } }}>
                <AppIcon size={isMobile ? 72 : 84} />
              </Box>

              <Typography
                component="h1"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.12,
                  letterSpacing: '-0.02em',
                  whiteSpace: 'pre-line',
                  fontSize: { xs: '1.85rem', sm: '2.35rem' },
                  mb: { xs: 1.75, sm: 2.25 },
                }}
              >
                {slide.title}
              </Typography>

              {isCompare ? (
                <Box
                  sx={{
                    mb: { xs: 3, sm: 3.5 },
                    textAlign: 'left',
                    border: `1px solid ${BRAND.cardRowBorder}`,
                    borderRadius: 2.5,
                    overflow: 'hidden',
                  }}
                >
                  {/* Header row */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1.55fr 1fr',
                      bgcolor: 'rgba(255,255,255,0.04)',
                    }}
                  >
                    <Box
                      sx={{
                        px: { xs: 1.5, sm: 2 },
                        py: 1.25,
                        borderRight: `1px solid ${BRAND.cardRowBorder}`,
                      }}
                    >
                      <Typography
                        sx={{ color: BRAND.blueBright, fontWeight: 800, fontSize: '0.9rem' }}
                      >
                        {t('onboarding.compare.porvenix')}
                      </Typography>
                    </Box>
                    <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 1.25 }}>
                      <Typography
                        sx={{ color: BRAND.textMuted, fontWeight: 700, fontSize: '0.9rem' }}
                      >
                        {t('onboarding.compare.sportsbooks')}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Feature rows */}
                  {[
                    {
                      feature: t('onboarding.compare.row1'),
                      value: t('onboarding.compare.no'),
                      bad: true,
                    },
                    {
                      feature: t('onboarding.compare.row2'),
                      value: t('onboarding.compare.no'),
                      bad: true,
                    },
                    {
                      feature: t('onboarding.compare.row3'),
                      value: t('onboarding.compare.fees'),
                      bad: true,
                    },
                  ].map((row, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1.55fr 1fr',
                        borderTop: `1px solid ${BRAND.cardRowBorder}`,
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="flex-start"
                        sx={{
                          px: { xs: 1.5, sm: 2 },
                          py: { xs: 1.4, sm: 1.6 },
                          borderRight: `1px solid ${BRAND.cardRowBorder}`,
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            color: BRAND.green,
                            fontWeight: 800,
                            fontSize: '0.95rem',
                            flexShrink: 0,
                            lineHeight: 1.35,
                          }}
                        >
                          ✓
                        </Box>
                        <Typography
                          sx={{
                            color: 'rgba(255,255,255,0.85)',
                            fontSize: { xs: '0.82rem', sm: '0.88rem' },
                            lineHeight: 1.35,
                          }}
                        >
                          {row.feature}
                        </Typography>
                      </Stack>
                      <Box sx={{ display: 'flex', alignItems: 'center', px: { xs: 1.5, sm: 2 } }}>
                        <Typography
                          sx={{
                            color: row.bad ? BRAND.noText : BRAND.textMuted,
                            fontWeight: row.bad ? 700 : 500,
                            fontSize: { xs: '0.82rem', sm: '0.88rem' },
                          }}
                        >
                          {row.value}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <>
                  <Typography
                    sx={{
                      color: BRAND.textSecondary,
                      fontSize: { xs: '0.95rem', sm: '1.02rem' },
                      lineHeight: 1.55,
                      maxWidth: 440,
                      mx: 'auto',
                      mb: { xs: 3, sm: 3.5 },
                    }}
                  >
                    {slide.description}
                  </Typography>

                  <Stack spacing={1.25} sx={{ mb: { xs: 3, sm: 3.5 }, textAlign: 'left' }}>
                    {slide.points.map((point, i) => (
                      <Stack
                        key={i}
                        direction="row"
                        alignItems="center"
                        spacing={1.5}
                        sx={{
                          bgcolor: BRAND.cardRow,
                          border: `1px solid ${BRAND.cardRowBorder}`,
                          borderRadius: 2,
                          px: { xs: 1.75, sm: 2.25 },
                          py: { xs: 1.5, sm: 1.75 },
                        }}
                      >
                        <Typography
                          sx={{
                            color: BRAND.blueBright,
                            fontWeight: 800,
                            fontSize: '1rem',
                            minWidth: 14,
                            flexShrink: 0,
                          }}
                        >
                          {i + 1}
                        </Typography>
                        <Typography
                          sx={{
                            color: 'rgba(255,255,255,0.82)',
                            fontSize: { xs: '0.9rem', sm: '0.95rem' },
                            lineHeight: 1.4,
                          }}
                        >
                          {point}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </>
              )}

              <Button
                fullWidth
                onClick={() => goTo(active + 1)}
                disableElevation
                sx={{
                  bgcolor: BRAND.blue,
                  color: '#ffffff',
                  fontWeight: 700,
                  fontSize: '1.02rem',
                  textTransform: 'none',
                  borderRadius: 2,
                  py: 1.6,
                  '&:hover': { bgcolor: BRAND.blueDark },
                }}
              >
                {ctaLabel}
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default OnboardingScreen;
